// Rotina de Coleta e Devolução de um Cliente (ADR-0014). Uma Parada = dias da
// semana + horário + ação. É o modelo único da rotina desde a Fase 3.

import { newId, type Id } from "./ids.ts";
import type { AcaoParada, Parada, Snapshot } from "./snapshot.ts";
import { coletaEm, type Lote } from "./lotes.ts";

export type { AcaoParada, Parada };

export interface DadosParada {
  diasSemana: number[];
  horario: string;
  acao: AcaoParada;
}

const HORA = /^([01]\d|2[0-3]):[0-5]\d$/;
const ACOES: readonly AcaoParada[] = ["coletar", "devolver", "ambos"];

export class ErroParada extends Error {}

function validar(dados: DadosParada): DadosParada {
  const dias = [...new Set(dados.diasSemana)]
    .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6)
    .sort((a, b) => a - b);
  if (dias.length === 0) {
    throw new ErroParada("Escolha ao menos um dia da semana.");
  }
  if (!HORA.test(dados.horario)) {
    throw new ErroParada("Horário inválido (use HH:MM).");
  }
  if (!ACOES.includes(dados.acao)) {
    throw new ErroParada("Ação da parada inválida (coletar, devolver ou ambos).");
  }
  return { diasSemana: dias, horario: dados.horario, acao: dados.acao };
}

/** Recusa uma Parada que colide com outra do mesmo Cliente no mesmo dia+horário. */
function conferirColisao(
  paradas: readonly Parada[],
  clienteId: Id,
  candidata: DadosParada,
  ignorarId?: Id,
): void {
  for (const p of paradas) {
    if (p.clienteId !== clienteId || p.id === ignorarId) continue;
    if (p.horario !== candidata.horario) continue;
    if (p.diasSemana.some((d) => candidata.diasSemana.includes(d))) {
      throw new ErroParada(
        `Já existe uma parada às ${candidata.horario} num desses dias.`,
      );
    }
  }
}

export function adicionarParada(
  snapshot: Snapshot,
  clienteId: Id,
  dados: DadosParada,
): Snapshot {
  if (!snapshot.clientes.some((c) => c.id === clienteId)) {
    throw new ErroParada("Cliente não encontrado.");
  }
  const campos = validar(dados);
  conferirColisao(snapshot.paradas, clienteId, campos);
  const parada: Parada = { id: newId(), clienteId, ...campos };
  return { ...snapshot, paradas: [...snapshot.paradas, parada] };
}

export function editarParada(
  snapshot: Snapshot,
  id: Id,
  dados: DadosParada,
): Snapshot {
  const atual = snapshot.paradas.find((p) => p.id === id);
  if (!atual) throw new ErroParada("Parada não encontrada.");
  const campos = validar(dados);
  conferirColisao(snapshot.paradas, atual.clienteId, campos, id);
  return {
    ...snapshot,
    paradas: snapshot.paradas.map((p) => (p.id === id ? { ...p, ...campos } : p)),
  };
}

export function removerParada(snapshot: Snapshot, id: Id): Snapshot {
  return { ...snapshot, paradas: snapshot.paradas.filter((p) => p.id !== id) };
}

/**
 * Substitui todas as Paradas de um Cliente pela lista informada. Usado quando o
 * Cliente e sua rotina são salvos juntos (modal de Cliente).
 */
export function definirParadasDoCliente(
  snapshot: Snapshot,
  clienteId: Id,
  lista: readonly DadosParada[],
): Snapshot {
  if (!snapshot.clientes.some((c) => c.id === clienteId)) {
    throw new ErroParada("Cliente não encontrado.");
  }
  // Preserva a identidade: uma Parada que continua igual (mesmo horário, ação e
  // dias) mantém o seu `id`, para os `lote.parada_id` que a referenciam não
  // virarem FK pendurada quando o Cliente é salvo de novo (ADR-0014). Só linhas
  // de fato novas ganham `newId()`.
  const anteriores = snapshot.paradas.filter((p) => p.clienteId === clienteId);
  const reusados = new Set<Id>();
  const mesmosDias = (a: readonly number[], b: readonly number[]): boolean =>
    a.length === b.length && a.every((d, i) => d === b[i]);
  const novas: Parada[] = [];
  for (const dados of lista) {
    const campos = validar(dados);
    conferirColisao(novas, clienteId, campos);
    const igual = anteriores.find(
      (p) =>
        !reusados.has(p.id) &&
        p.horario === campos.horario &&
        p.acao === campos.acao &&
        mesmosDias(p.diasSemana, campos.diasSemana),
    );
    if (igual) reusados.add(igual.id);
    novas.push({ id: igual?.id ?? newId(), clienteId, ...campos });
  }
  return {
    ...snapshot,
    paradas: [
      ...snapshot.paradas.filter((p) => p.clienteId !== clienteId),
      ...novas,
    ],
  };
}

export function paradasDoCliente(
  snapshot: Pick<Snapshot, "paradas">,
  clienteId: Id,
): Parada[] {
  return snapshot.paradas.filter((p) => p.clienteId === clienteId);
}

/** As Paradas em que a lavanderia coleta (ação "coletar" ou "ambos") — as
 * que podem ser Origem da Coleta de uma OS. */
export function paradasDeColeta(snapshot: Pick<Snapshot, "paradas">, clienteId: Id): Parada[] {
  return paradasDoCliente(snapshot, clienteId).filter((p) => p.acao === "coletar" || p.acao === "ambos");
}

const DIAS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
export function descreveDias(dias: number[]): string {
  return dias.map((d) => DIAS[d] ?? "?").join(", ");
}

const ENTREGA = new Set<AcaoParada>(["devolver", "ambos"]);
const minutos = (hhmm: string): number => {
  const [h, m] = hhmm.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

/**
 * Primeira ocorrência de uma Parada de devolução (`devolver` ou `ambos`) a
 * partir de `coleta`. Sem folga (`devolucaoNoMesmoDia = false`), o mesmo dia da
 * coleta não conta; com folga, conta se o horário for posterior ao da coleta.
 * `null` quando o Cliente não tem nenhuma Parada de devolução.
 */
export function proximaDevolucao(
  paradas: readonly Parada[],
  coleta: Date,
  devolucaoNoMesmoDia: boolean,
): Date | null {
  const entregas = paradas.filter((p) => ENTREGA.has(p.acao));
  if (entregas.length === 0) return null;
  const minColeta = coleta.getHours() * 60 + coleta.getMinutes();

  for (let offset = 0; offset <= 13; offset++) {
    const dia = new Date(coleta.getFullYear(), coleta.getMonth(), coleta.getDate() + offset);
    const wd = dia.getDay();
    let melhor: number | null = null;
    for (const p of entregas) {
      if (!p.diasSemana.includes(wd)) continue;
      const mp = minutos(p.horario);
      if (offset === 0 && !(devolucaoNoMesmoDia && mp > minColeta)) continue;
      if (melhor == null || mp < melhor) melhor = mp;
    }
    if (melhor != null) {
      dia.setHours(Math.floor(melhor / 60), melhor % 60, 0, 0);
      return dia;
    }
  }
  return null;
}

/** Data/hora prevista da devolução de um Lote — `null` se não dá para calcular. */
export function previstoDoLote(
  snapshot: Pick<Snapshot, "clientes" | "paradas">,
  lote: Lote,
): Date | null {
  if (lote.previstoManual) {
    const [data, hora] = lote.previstoManual.split(" ");
    const [y, mo, d] = (data ?? "").split("-").map(Number);
    const [h, mi] = (hora ?? "").split(":").map(Number);
    return new Date(y ?? 1970, (mo ?? 1) - 1, d ?? 1, h ?? 0, mi ?? 0, 0, 0);
  }
  const cliente = snapshot.clientes.find((c) => c.id === lote.clienteId);
  if (!cliente) return null;
  return proximaDevolucao(
    paradasDoCliente(snapshot, lote.clienteId),
    coletaEm(lote),
    cliente.devolucaoNoMesmoDia,
  );
}

/** Veredito de atraso entre um horário previsto e o horário real. */
export function atrasoEntre(
  previsto: Date,
  quando: Date,
): { atrasado: boolean; minutos: number } {
  const diff = Math.round((quando.getTime() - previsto.getTime()) / 60000);
  return { atrasado: diff > 0, minutos: Math.max(0, diff) };
}

/**
 * `null` se o Lote não foi devolvido, não há previsto, ou a devolução não tem
 * hora registrada — sem `devolvidoHora` não há como julgar o horário.
 */
export function atrasoDoLote(
  snapshot: Pick<Snapshot, "clientes" | "paradas">,
  lote: Lote,
): { atrasado: boolean; minutos: number } | null {
  if (!lote.devolvidoEm) return null;
  const previsto = previstoDoLote(snapshot, lote);
  if (!previsto) return null;
  if (!lote.devolvidoHora) return null;
  const [y, mo, d] = lote.devolvidoEm.split("-").map(Number);
  const [h, mi] = lote.devolvidoHora.split(":").map(Number);
  const dev = new Date(y ?? 1970, (mo ?? 1) - 1, d ?? 1, h ?? 0, mi ?? 0, 0, 0);
  return atrasoEntre(previsto, dev);
}

export function descreveAtraso(minutosAtraso: number): string {
  if (minutosAtraso <= 0) return "No prazo";
  const h = Math.floor(minutosAtraso / 60);
  const m = minutosAtraso % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}
